import { Dashboard as DashboardIcon } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { SimpleDialog } from 'src/components/modal';
import { ProductForm } from 'src/components/productForm';
import { ProductVariationForm } from 'src/components/productVariationForm';
import { Snackbar } from 'src/components/snackbar';
import { UploadImageButton } from 'src/components/uploadImageButton';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { PATHS } from 'src/constants/paths';
import {
  useCreateProduct,
  useGetCCAssets,
  useGetThemes,
  useModal,
} from 'src/hooks';
import { useNavigateBlocker } from 'src/hooks/navigation';
import { IProduct, IProductVariation } from 'src/interfaces';
import { hasUniqueSizeAndFlavorVariations, lang, useUserRole } from 'src/utils';

import {
  Container,
  Content,
  Divider,
  LeftContainer,
  Section,
  StyledDiscardButton,
} from './AddProductPage.styled';
import { useAddProductPage } from './hooks/AddProductPage.hook';

export const AddProductPage = () => {
  const discardModal = useModal();
  const saveProductModal = useModal();
  const showSavedModal = useModal();
  const { isViewOnly } = useUserRole();
  const createProduct = useCreateProduct();
  const { data: themes } = useGetThemes();
  const { data: variationAssets = [] } = useGetCCAssets();
  const { formMethod, fieldArray } = useAddProductPage();
  const navigate = useNavigate();
  const [newVariationIndex, setNewVariationIndex] = useState(-1);
  const skipBlockRef = useRef(false);

  const skipNextBlock = useCallback(() => {
    skipBlockRef.current = true;
  }, []);

  // Subscribe during render so RHF actually updates isDirty (Proxy optimization).
  const { isDirty } = formMethod.formState;
  const productAsset = formMethod.watch('asset');
  useNavigateBlocker(
    () =>
      !skipBlockRef.current &&
      isDirty &&
      !createProduct.isLoading &&
      !showSavedModal.open,
  );

  const onAddVariation = () => {
    // Check if 'Product Code' is empty
    const LAST = -1;
    const productValue = formMethod.getValues().productVariations;
    const lastProductCode = productValue.at(LAST)?.product_code;

    if (!lastProductCode?.trim()) {
      Snackbar.error(lang.productCodeMissing);

      return;
    }

    setNewVariationIndex(fieldArray.fields.length);
    fieldArray.append({
      active: true,
      nutrition: '000',
      volume: 0,
    } as unknown as IProductVariation);
  };

  const onSaved = async (values: IProduct & { asset?: File }) => {
    const trimmedNameOverride = values.name_override
      ? values.name_override.trim()
      : null;

    // Trim the name field for each product variation
    const trimmedProductVariations = values.productVariations.map(
      (variation) => ({
        ...variation,
        name: variation.name.trim(),
      }),
    );

    /* Shouldn't have more than one variation with the same size same flavor */
    const isValid = hasUniqueSizeAndFlavorVariations(trimmedProductVariations);

    if (!isValid) {
      Snackbar.error('Product variation size and flavor must be unique');

      return;
    }

    // Update the values with trimmed fields
    const trimmedValues = {
      ...values,
      name_override: trimmedNameOverride,
      productVariations: trimmedProductVariations,
      allVariationAssets: variationAssets,
    };
    await createProduct.mutateAsync(trimmedValues);
    setNewVariationIndex(-1);

    Snackbar.success(`${values.product_code} ${lang.successfullyAdded}`);

    saveProductModal.onCloseModal();
    showSavedModal.onOpenModal();
  };

  const handleSaveConfirm = formMethod.handleSubmit((values) => {
    saveProductModal.onOpenModal(() => onSaved(values));
  });

  return (
    <FormProvider {...formMethod}>
      <Container>
        <Box sx={{ position: 'absolute', top: 0, right: 0, margin: 3 }}>
          {isViewOnly && <ViewOnlyAlert />}
        </Box>
        <Typography variant="h1" gutterBottom>
          {lang.addNewProduct}
        </Typography>

        <Content>
          <Section>
            <LeftContainer>
              <UploadImageButton />
            </LeftContainer>

            <ProductForm />
          </Section>

          {fieldArray.fields.map((productVariation, index) => {
            return (
              <Box key={productVariation.uid}>
                <Divider />
                <Section>
                  <LeftContainer />
                  <ProductVariationForm
                    themes={themes}
                    index={index}
                    isProductCodeEnabled={index === newVariationIndex}
                    hideProductVariationMenu={true}
                    groupProductImage={productAsset}
                  />
                </Section>
              </Box>
            );
          })}
        </Content>

        <Divider />

        {!isViewOnly && (
          <Section>
            <Button
              startIcon={<DashboardIcon />}
              variant="outlined"
              onClick={onAddVariation}>
              {lang.addVariation}
            </Button>

            <Section sx={{ gap: '10px' }}>
              <StyledDiscardButton
                variant="text"
                onClick={discardModal.onOpenModal}>
                {lang.discard}
              </StyledDiscardButton>

              <Button disabled={isViewOnly} onClick={handleSaveConfirm}>
                {lang.save}
              </Button>
            </Section>
          </Section>
        )}
      </Container>
      {discardModal.open && (
        <SimpleDialog
          actionColor="error"
          open={discardModal.open}
          onClose={discardModal.onCloseModal}
          onClickCancel={discardModal.onCloseModal}
          onClickConfirm={() => {
            skipNextBlock();
            navigate(PATHS.PRODUCTS);
          }}
          title={lang.discardModal.title}
          subTitle={lang.discardModal.subTitle}
          cancelText={lang.cancel}
          confirmText={lang.discard}
        />
      )}
      {saveProductModal.open && (
        <SimpleDialog
          open={saveProductModal.open}
          onClose={saveProductModal.onCloseModal}
          onClickCancel={saveProductModal.onCloseModal}
          onClickConfirm={saveProductModal.customFn}
          title={lang.titleTextSave}
          cancelText={lang.cancel}
          confirmText={lang.confirm}
          isLoading={createProduct.isLoading}
          modalSize="sm">
          <Typography gutterBottom>{lang.subTitlePublishConfirm}</Typography>
        </SimpleDialog>
      )}
      {showSavedModal.open && (
        <SimpleDialog
          open={showSavedModal.open}
          onClose={() => {
            skipNextBlock();
            navigate(PATHS.PRODUCT_EDIT(formMethod.getValues().product_code));
          }}
          modalSize="sm"
          title={lang.titleTextAfterSave}
          cancelText={lang.close}
          confirmText={lang.goToPublications}
          onClickCancel={() => {
            skipNextBlock();
            navigate(PATHS.PRODUCT_EDIT(formMethod.getValues().product_code));
          }}
          onClickConfirm={() => {
            skipNextBlock();
            navigate(PATHS.PRODUCT_PUBLICATIONS);
          }}>
          <Typography gutterBottom>
            {lang.subTitleConfirmProductPublication}
          </Typography>
        </SimpleDialog>
      )}
    </FormProvider>
  );
};
